import { useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PlansResponse } from '../types/plans/types';
import {
  PracticesResponse,
  ProvidersResponse,
} from '../types/practices';

type PaginationResponse = PracticesResponse | ProvidersResponse | PlansResponse
interface FetchDataFunction<FT extends PaginationResponse> {
  (
    startToken: string | null,
    filters?: Record<string, string | string[] | undefined>,
    limit?: number
  ): Promise<{
    data: FT | null;
  }>;
}

interface PaginationHookReturn<T> {
  items: T[];
  loading: boolean;
  error: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  handlePageChange: (pageNumber: number) => void;
  setItems: (items: T[] | ((prevItems: T[]) => T[])) => void;
  refetch: () => void;
}

interface PaginationOptions {
  useClientSidePagination?: boolean;
  // Called when a fetch is skipped because the current filters already match
  // the last successful fetch. Lets callers (e.g. the Expand All queue) treat
  // a cache hit the same as a load settling, so the queue doesn't stall.
  onSkipped?: () => void;
}

export const usePagination = <T, FT extends PaginationResponse>(
  fetchDataFunction: FetchDataFunction<FT>,
  itemsPerPage: number,
  options: PaginationOptions = {},
  filters?: Record<string, string | string[] | undefined>,
  enabled: boolean = true
): PaginationHookReturn<T> => {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const toast = useToast();

  // Store all fetched data for client-side pagination
  const [allItems, setAllItems] = useState<T[]>([]);
  const [fetchError, setFetchError] = useState(false);

  // Always-current ref so the skip callback is never stale inside the effect
  const onSkippedRef = useRef(options.onSkipped);
  onSkippedRef.current = options.onSkipped;

  // Use refs to store values and prevent unnecessary re-renders
  const fetchDataFunctionRef = useRef(fetchDataFunction);
  // Synchronous in-flight flag — lastFetchedFiltersRef is set before the
  // request resolves, so the skip condition can match while a fetch is still
  // in flight. Without this guard, onSkipped would fire and release a queue
  // slot even though the request hasn't completed.
  const inFlightRef = useRef(false);
  const filtersRef = useRef<string>('');
  const lastFetchedFiltersRef = useRef<string>('');

  useEffect(() => {
    fetchDataFunctionRef.current = fetchDataFunction;
  }, [fetchDataFunction]);

  // Store filters as stringified ref to compare changes
  useEffect(() => {
    filtersRef.current = JSON.stringify(filters || {});
  }, [filters]);

  const updatePagination = useCallback(
    (totalItems: number) => {
      setTotalCount(totalItems);
      const pages = Math.ceil(totalItems / itemsPerPage);
      setTotalPages(pages);
    },
    [itemsPerPage]
  );

  const fetchItems = useCallback(
    async (
      token: string | null,
      pageNumber: number = 1,
      filters?: Record<string, string | string[] | undefined>
    ) => {
      setLoading(true);
      inFlightRef.current = true;
      setFetchError(false);
      try {
        const response = await fetchDataFunctionRef.current(
          token,
          filters,
          itemsPerPage
        );
        if (response && response.data) {
          const newData = response.data;

          if ('practices' in newData) {
            const practices = newData.practices as T[];

            if (options.useClientSidePagination) {
              // Client-side: store all data and slice
              setAllItems(practices);
              const paginatedPractices = practices.slice(
                0,
                itemsPerPage
              );
              setItems(paginatedPractices);
              updatePagination(practices.length);
            } else {
              // Server-side: use data as-is for current page
              setItems(practices);
              updatePagination(newData.totalPractices);
              setCurrentPage(pageNumber);
            }
          } else if ('providers' in newData) {
            const providers = newData.providers as T[];

            if (options.useClientSidePagination) {
              // Client-side: store all data and slice
              setAllItems(providers);
              const paginatedProviders = providers.slice(
                0,
                itemsPerPage
              );
              setItems(paginatedProviders);
              updatePagination(providers.length);
            } else {
              // Server-side: use data as-is for current page
              setItems(providers);
              updatePagination(newData.totalProviders);
              setCurrentPage(pageNumber);
            }
          } else if ('insurancePlans' in newData) {
            const plans = newData.insurancePlans as T[];

            if (options.useClientSidePagination) {
              // Client-side: store all data and slice
              setAllItems(plans);
              const paginatedPlans = plans.slice(0, itemsPerPage);
              setItems(paginatedPlans);
              updatePagination(plans.length);
            } else {
              // Server-side: use data as-is for current page
              setItems(plans);
              updatePagination(newData.totalPlans);
              setCurrentPage(pageNumber);
            }
          }
        } else {
          toast({
            title: 'No data received',
            description: 'The data fetch returned no results.',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
          setItems([]);
          setAllItems([]);
          updatePagination(0);
        }
      } catch (error) {
        toast({
          title: 'Failed to fetch data',
          description:
            'An error occurred while fetching data. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setItems([]);
        setAllItems([]);
        updatePagination(0);
        setFetchError(true);
      } finally {
        inFlightRef.current = false;
        setLoading(false);
      }
    },
    [
      updatePagination,
      toast,
      itemsPerPage,
      options.useClientSidePagination,
    ]
  );

  useEffect(() => {
    if (!enabled) return;

    const currentFiltersString = filtersRef.current;

    // Allow first fetch (when lastFetchedFiltersRef is empty)
    // Only skip if filters haven't actually changed (by content, not reference)
    if (
      lastFetchedFiltersRef.current !== '' &&
      currentFiltersString === lastFetchedFiltersRef.current
    ) {
      // Filters already match the last successful fetch — no new request needed.
      // If a request for these filters is still in flight, the queue slot will
      // be released naturally when it settles (loading true→false). Only
      // notify onSkipped for a true cache hit where no request is in flight.
      if (!inFlightRef.current) {
        onSkippedRef.current?.();
      }
      return;
    }

    lastFetchedFiltersRef.current = currentFiltersString;
    fetchItems(null, 1, filters);
  }, [fetchItems, filters, enabled]);

  const handlePageChange = (pageNumber: number) => {
    if (
      pageNumber !== currentPage &&
      pageNumber >= 1 &&
      pageNumber <= totalPages
    ) {
      if (options.useClientSidePagination) {
        // Client-side pagination: slice from allItems
        setCurrentPage(pageNumber);
        const startIndex = (pageNumber - 1) * itemsPerPage;
        const endIndex = Math.min(
          startIndex + itemsPerPage,
          allItems.length
        );
        const pageItems = allItems.slice(startIndex, endIndex);
        setItems(pageItems);
      } else {
        // Server-side pagination: fetch new data for this page
        // Calculate the startToken based on the page number
        const startToken =
          pageNumber === 1
            ? null
            : ((pageNumber - 1) * itemsPerPage).toString();
        fetchItems(startToken, pageNumber, filters);
      }
    }
  };

  const refetch = useCallback(() => {
    setFetchError(false);
    setCurrentPage(1);
    // Reset filter tracking to allow refetch
    lastFetchedFiltersRef.current = '';
    fetchItems(null, 1, filters);
  }, [fetchItems, filters]);

  return {
    items,
    loading,
    error: fetchError,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
    setItems,
    refetch,
  };
};
